import {
  WSButton,
  WSElement,
  WSEmptyState,
  WSPillProps,
  WSSectionToolbar,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { useHistory } from "react-router-dom";

import {
  PayeeEngagement,
  PayeeEngagementPaymentsEligibility,
  PayeeEngagementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeEngagements } from "@query";
import { WSQueries } from "../../../../query/WSQuery";
import { formatNumber } from "accounting";
import { useCreatePayeeEngagementModal } from "../../../Payees/components/CreatePayeeEngagementModal/useCreateEngagementModal";
import queryString from "qs";
import { PAYEES_ALL_PATH } from "../../../Payees/utils";

export interface RecentContractorsWidgetProps {
  engagementId: string;
}

type TableItem = {
  count: number;
  statusPill: WSPillProps;
};

// The app's "archive" action is `suspend` (reversible — `terminate` is terminal and
// would strand the restore flow), so both non-live states read as archived here.
const isArchived = (payeeEngagement: PayeeEngagement) =>
  payeeEngagement.status === PayeeEngagementStatus.SUSPENDED ||
  payeeEngagement.status === PayeeEngagementStatus.TERMINATED;

export const RecentContractorsWidget: React.FC<
  RecentContractorsWidgetProps
> = ({ engagementId }) => {
  const history = useHistory();

  const addEngagementModal = useCreatePayeeEngagementModal();

  // Scoped server-side to this engagement (SDK 3.3.0 `filter.engagementId`), then
  // bucketed client-side: the eligible/ineligible split rides `paymentsEligibility`,
  // which is not a filter option, so the three counts can't each be a `totalSize`
  // read. One request over one template's assignments rather than the whole account.
  const queryEngagements =
    queriesPayeeEngagements.listPayeeEngagementsByPayer.useQueryAll({
      filter: { engagementId: { eq: engagementId } }
    });

  return (
    <WSQueries queries={{ queryEngagements }}>
      {({ queryEngagements: { flatData } }) => {
        const engagementsForType = flatData ?? [];

        const archived = engagementsForType.filter(isArchived).length;
        // v1 keyed eligibility off `status`, which only had Active/Inactive — so the
        // old "ineligible" bucket (status notIn [Active, Inactive]) was always empty.
        // v3 exposes the real signal as `paymentsEligibility`, so the split is now
        // meaningful rather than always zero.
        const eligible = engagementsForType.filter(
          payeeEngagement =>
            !isArchived(payeeEngagement) &&
            payeeEngagement.paymentsEligibility ===
              PayeeEngagementPaymentsEligibility.ELIGIBLE
        ).length;
        const ineligible = engagementsForType.length - archived - eligible;

        const all = engagementsForType.length;

        const tableData: TableItem[] = [
          {
            count: eligible,
            statusPill: {
              text: "Eligible",
              theme: "success",
              icon: true
            }
          },
          {
            count: ineligible,
            statusPill: {
              text: "Ineligible",
              theme: "warning",
              icon: true
            }
          },
          {
            count: archived,
            statusPill: {
              text: "Archived",
              theme: "neutral",
              icon: "archive"
            }
          }
        ];

        if (all === 0) {
          return (
            <WSElement>
              <WSSectionToolbar
                mb="S"
                title="Contractors"
                button={{
                  label: "Add assignment",
                  kind: "Link",
                  onClick: () => {
                    addEngagementModal.open(
                      { engagementId },
                      { title: "Add assignment" }
                    );
                  }
                }}
              />
              <WSEmptyState
                type="people"
                title="No assignments yet"
                description="Once an assignment has been added for a contractor, it will appear here."
                hasBorder
              />
            </WSElement>
          );
        }
        return (
          <WSElement>
            <WSSectionToolbar
              mb="S"
              title="Assignments"
              button={{
                label: "Add assignment",
                kind: "Link",
                onClick: () => {
                  addEngagementModal.open(
                    { engagementId },
                    { title: "Add assignment" }
                  );
                }
              }}
            />

            <WSTable<TableItem>
              tableData={tableData.map((item, index) => ({
                id: `${item.statusPill.text} ${index}`,
                data: item
              }))}
              columns={[
                {
                  config: {
                    gridTemplateSizeMin: "0",
                    gridTemplateSizeMax: "1fr",
                    header: {
                      text: "Contractors"
                    }
                  },
                  renderFunction: ({ data }) => {
                    return <WSTableCell text={formatNumber(data.count)} />;
                  }
                },
                {
                  config: {
                    gridTemplateSizeMin: "0",
                    gridTemplateSizeMax: "1fr",
                    header: {
                      text: "Status"
                    }
                  },
                  renderFunction: ({ data }) => {
                    return <WSTableCell pill={data.statusPill} />;
                  }
                }
              ]}
            />

            <WSButton.Link
              mt="S"
              onClick={() => {
                const query = queryString.stringify({ engagementId });
                history.push(`${PAYEES_ALL_PATH}?${query}`);
              }}
            >
              View all
            </WSButton.Link>
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
